import { Fragment, useCallback, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import {
  DialogSelectTriggerContent,
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
} from 'components/ui';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import type { DashboardFieldOption } from '../dashboardEditFields.types';
import { DashboardSelectTrigger } from './dashboardControls.styles';

// The column vocabularies are ~150 options and now carry disabled rows, so a 10-row window is very
// likely to contain nothing the user wants. buildColumnOptions sorts selectable rows ahead of
// disabled ones within each group, and PrimaryNavigationMenu already scrolls, so a larger window is
// simply more useful. Small enums (comparison, direction, ...) are unaffected: they render whole.
const VISIBLE_LIMIT = 25;
const MENU_MIN_WIDTH = 240;
// Wider than the Documents copy: metric labels run to "Total fully diluted preferred shares", which
// truncates badly at 320.
const MENU_MAX_WIDTH = 420;

// "Not set" is a real option with a null id rather than a sentinel string, so picking it can delete
// the key outright instead of writing an empty value the backend would have to interpret.
const EMPTY_OPTION_ID = '__unset__';

interface DashboardEnumSelectProps {
  disabled?: boolean;
  emptyLabel?: string;
  // A required field has no "Not set" entry; the blank placeholder only shows for a legacy row that
  // is already missing the key.
  isRequired?: boolean;
  onChange: (next: string | undefined) => void;
  options: readonly DashboardFieldOption[];
  placeholder?: string;
  searchable?: boolean;
  testId?: string;
  value: string | undefined;
}

// components/ui exports no single-select, and the only precedent in the app
// (pages/Documents/.../FirmDocumentDialogSearchableSelect) is number-keyed and private to that
// feature. This is the same composition generalized to string ids. A third copy should be promoted
// to components/ui rather than copied again.
const DashboardEnumSelect = ({
  disabled = false,
  emptyLabel = 'Not set',
  isRequired = false,
  onChange,
  options,
  placeholder = 'Select…',
  searchable = false,
  testId,
  value,
}: DashboardEnumSelectProps) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const selectableOptions = isRequired
    ? options
    : [{ label: emptyLabel, value: EMPTY_OPTION_ID }, ...options];

  const {
    canShowFewerOptions,
    filteredOptions,
    hasMoreOptions,
    hiddenOptionCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions,
  } = useSearchableLimitedOptions({
    getSearchText: option => option.label,
    options: selectableOptions,
    // A small enum shows every entry at once; only the 93/113/115-name vocabularies need paging.
    visibleLimit: searchable ? VISIBLE_LIMIT : selectableOptions.length,
  });

  const isMenuOpen = Boolean(anchorEl);
  const selectedOption = options.find(option => option.value === value);
  const triggerWidth = anchorEl?.getBoundingClientRect().width ?? 0;
  const menuMinWidth = Math.max(MENU_MIN_WIDTH, Math.ceil(triggerWidth));
  const menuMaxWidth = Math.max(MENU_MAX_WIDTH, menuMinWidth);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    resetSearchableOptions();
  }, [resetSearchableOptions]);

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSelect = (nextValue: string, option: DashboardFieldOption) => {
    // The row is only aria-disabled, so it still fires onClick. Refusing here is what makes it
    // inert. A legacy option — one the stored definition already names — carries a reason but is
    // deliberately still selectable, so the user can re-pick it after browsing away.
    if (option.disabledReason && !option.isLegacy) {
      return;
    }

    onChange(nextValue === EMPTY_OPTION_ID ? undefined : nextValue);
    handleMenuClose();
  };

  return (
    <>
      <DashboardSelectTrigger
        aria-expanded={isMenuOpen || undefined}
        aria-haspopup="listbox"
        data-testid={testId}
        disabled={disabled}
        hasValue={Boolean(selectedOption)}
        onClick={handleTriggerClick}>
        <DialogSelectTriggerContent
          placeholder={isRequired ? placeholder : emptyLabel}
          value={selectedOption?.label}
        />
      </DashboardSelectTrigger>
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        disableAutoFocusItem
        menuMaxWidth={menuMaxWidth}
        menuMinWidth={menuMinWidth}
        onClose={handleMenuClose}
        open={isMenuOpen}
        MenuListProps={{ disableListWrap: true }}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}>
        {searchable && (
          <PrimaryNavigationMenuSearch
            clearAriaLabel="Clear search"
            fullWidth
            inputRef={searchInputRef}
            onChange={event => setSearchValue(event.target.value)}
            onClear={() => setSearchValue('')}
            placeholder="Search"
            value={searchValue}
          />
        )}
        {visibleOptions.map((option, index) => (
          <Fragment key={option.value}>
            {option.group && option.group !== visibleOptions[index - 1]?.group && (
              <PrimaryNavigationMenuItem disabled isHeader>
                {option.group}
              </PrimaryNavigationMenuItem>
            )}
            <PrimaryNavigationMenuItem
              // aria-disabled, not MUI's `disabled`: a disabled MenuItem leaves the keyboard
              // sequence, which would put the reason out of reach of the readers most likely to
              // need it. handleSelect refuses the value instead.
              aria-disabled={option.disabledReason ? 'true' : undefined}
              isActive={option.value === (value ?? EMPTY_OPTION_ID)}
              onClick={() => handleSelect(option.value, option)}
              secondaryLabel={option.disabledReason}>
              {option.label}
            </PrimaryNavigationMenuItem>
          </Fragment>
        ))}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenOptionCount}
            itemLabelPlural="options"
            itemLabelSingular="option"
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural="options"
            itemLabelSingular="option"
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {filteredOptions.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            No matches
          </PrimaryNavigationMenuItem>
        )}
      </PrimaryNavigationMenu>
    </>
  );
};

export default DashboardEnumSelect;
